import { useState } from "react";
import { Gift, CreditCard, Mail, Heart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const giftCardAmounts = [
  { value: "50", label: "$50" },
  { value: "100", label: "$100" },
  { value: "150", label: "$150" },
  { value: "200", label: "$200" },
  { value: "250", label: "$250" },
  { value: "300", label: "$300" },
  { value: "350", label: "$350" },
  { value: "400", label: "$400" },
  { value: "450", label: "$450" },
  { value: "500", label: "$500" },
];

const GiftCardSection = ({ isVisible }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    recipientName: "",
    recipientEmail: "",
    senderName: "",
    senderEmail: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmountChange = (value) => {
    setFormData((prev) => ({ ...prev, amount: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.recipientName || !formData.recipientEmail || !formData.senderName || !formData.senderEmail) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/gift-card`, formData);
      toast.success("Gift card request submitted! We'll send payment instructions to your email.");
      setFormData({
        amount: "",
        recipientName: "",
        recipientEmail: "",
        senderName: "",
        senderEmail: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting gift card:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="gift-cards"
      className="py-24 lg:py-32 bg-[#FDFBF9]"
      data-testid="gift-card-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image and Info */}
          <div
            className={`${
              isVisible ? "animate-fade-in-up" : "opacity-0"
            }`}
          >
            <div className="relative mb-8">
              <img
                src="https://static.wixstatic.com/media/3a96ea_c07e0240beaa43c1a5d42e60a8386671~mv2.jpg/v1/fill/w_800,h_533,al_c,q_85,enc_auto/3a96ea_c07e0240beaa43c1a5d42e60a8386671~mv2.jpg"
                alt="Brittany Bailey Photography Gift Card"
                className="w-full h-auto object-cover"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#E2CCC2] -z-10" />
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Perfect Gift</p>
                  <p className="font-body text-sm text-[#5C5A56]">Give the gift of memories that last a lifetime</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Instant Delivery</p>
                  <p className="font-body text-sm text-[#5C5A56]">eGift card delivered directly to their inbox</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Any Session Type</p>
                  <p className="font-body text-sm text-[#5C5A56]">Can be used for weddings, portraits, family sessions & more</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div
            className={`${
              isVisible ? "animate-fade-in-up animation-delay-200" : "opacity-0"
            }`}
          >
            <p className="overline text-[#C8A99C] mb-4">Give the Gift of Memories</p>
            <h2 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-[#2C2A28] mb-6">
              eGift Cards
            </h2>
            <div className="section-divider mb-8" />

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="gift-card-form">
              {/* Amount Selection */}
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <CreditCard size={14} />
                  Select Amount *
                </Label>
                <Select
                  value={formData.amount}
                  onValueChange={handleAmountChange}
                >
                  <SelectTrigger
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="gift-card-amount-select"
                  >
                    <SelectValue placeholder="Choose gift card amount" />
                  </SelectTrigger>
                  <SelectContent>
                    {giftCardAmounts.map((amount) => (
                      <SelectItem key={amount.value} value={amount.value}>
                        {amount.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recipient Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#5C5A56]">
                    Recipient Name *
                  </Label>
                  <Input
                    name="recipientName"
                    type="text"
                    value={formData.recipientName}
                    onChange={handleChange}
                    placeholder="Who is this for?"
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="gift-card-recipient-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#5C5A56]">
                    Recipient Email *
                  </Label>
                  <Input
                    name="recipientEmail"
                    type="email"
                    value={formData.recipientEmail}
                    onChange={handleChange}
                    placeholder="their@email.com"
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="gift-card-recipient-email"
                  />
                </div>
              </div>

              {/* Sender Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#5C5A56]">
                    Your Name *
                  </Label>
                  <Input
                    name="senderName"
                    type="text"
                    value={formData.senderName}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="gift-card-sender-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-body text-sm text-[#5C5A56]">
                    Your Email *
                  </Label>
                  <Input
                    name="senderEmail"
                    type="email"
                    value={formData.senderEmail}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="gift-card-sender-email"
                  />
                </div>
              </div>

              {/* Personal Message */}
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56]">
                  Personal Message (Optional)
                </Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Add a personal note to make it extra special..."
                  rows={3}
                  className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none resize-none"
                  data-testid="gift-card-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#2C2A28] text-[#FDFBF9] font-body text-sm tracking-wide hover:bg-[#5C5A56] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="gift-card-submit"
              >
                {isSubmitting ? (
                  "Processing..."
                ) : (
                  <>
                    <Gift size={16} />
                    Purchase Gift Card
                  </>
                )}
              </button>

              <p className="text-center font-body text-xs text-[#5C5A56]">
                After submitting, you'll receive payment instructions via email. Gift card will be sent to recipient upon payment confirmation.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GiftCardSection;
